/**
 * PlatformContextCard
 * -------------------
 *
 * The dashboard lives at the platform apex: `useRedirectToApex('/dashboard')`
 * sends a tenant-nested URL back to the bare apex, and the surrounding nav
 * switches to the platform nav. Signing in at `/costem/login` still lands here,
 * so a tenant member is moved to the apex by the act of signing in.
 *
 * This card says so, and gives them the way back in. Without it the page states
 * no context at all and the only route into an ecosystem is the directory.
 *
 * Named for its job rather than its contents because
 * `features/profile/components/YourEcosystems.jsx` already owns that name and is
 * a superset of this: it manages membership (join, leave, browse joinable). This
 * one only orients the user and hands them a door.
 *
 * **The destination is `/profile`, NOT `/dashboard`.** The dashboard is
 * apex-only, so a full page load at `/{slug}/dashboard` resolves the tenant,
 * `useRedirectToApex` fires, and the user is bounced straight back to the page
 * they clicked from. The profile route renders in tenant context and is what the
 * sibling component links to for the same reason.
 *
 * **Cross-tenant navigation must be a full page load, not a router link.**
 * `EcosystemProvider` resolves the active tenant once on mount with
 * `useEffect(..., [])`. A SPA push to `/{slug}` would not re-bootstrap it: the
 * router basename would stay empty and every subsequent link would build an
 * apex URL while the user believed they were inside a tenant. `EcosystemChooser`
 * carries the same constraint, and `getEcosystemDestinationUrl` is the shared
 * helper that handles custom domains and subdomain mode alongside path mode.
 *
 * **Membership rows are nested, not flat.** `GET /api/users/me/memberships`
 * returns `{ ecosystem: { slug, name, ... }, role, status, joinedAt }`: only
 * `role` sits at the top level. Reading `m.slug` yields `undefined`, which
 * `getEcosystemDestinationUrl` turns into an empty string rather than an error,
 * so the bug renders as every card linking to the apex with no name on it.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Card, Col, Row, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';

import { getEcosystemDestinationUrl } from '@/shared/utils/ecosystemRouting';

export default function PlatformContextCard({ memberships, loading, error }) {
    // The controller drops a row whose ecosystem is missing or archived, but
    // guard anyway: a row without one has nowhere to link to.
    const entries = memberships.filter((m) => m?.ecosystem?.slug);

    return (
        <Card className="border-0 shadow-sm mb-4">
            <Card.Body className="p-4">
                <h5 className="text-primary mb-1">Your ecosystems</h5>
                <p className="text-muted small mb-3">
                    You are at the platform level, so no ecosystem is selected.
                    Open one to work inside it.
                </p>

                {loading && (
                    <div className="text-center py-3">
                        <Spinner
                            animation="border"
                            size="sm"
                            variant="primary"
                        />
                    </div>
                )}

                {!loading && error && (
                    <p className="text-muted small mb-0">{error}</p>
                )}

                {!loading && !error && entries.length === 0 && (
                    <p className="text-muted small mb-0">
                        You have not joined an ecosystem yet.{' '}
                        <Link to="/ecosystems">Browse the ecosystems</Link>{' '}
                        hosted on RILE Connect.
                    </p>
                )}

                {!loading && !error && entries.length > 0 && (
                    <Row className="g-3">
                        {entries.map((m) => (
                            <Col xs={12} md={6} key={m.ecosystem.slug}>
                                {/* A plain anchor, and /profile rather than
                                    /dashboard: see the notes at the top of this
                                    file. Either one wrong sends the user back
                                    where they started. */}
                                <a
                                    href={`${getEcosystemDestinationUrl(m.ecosystem)}/profile`}
                                    className="d-block border rounded p-3 h-100 text-decoration-none"
                                >
                                    <span className="d-block fw-bold text-primary">
                                        {m.ecosystem.name || m.ecosystem.slug}
                                    </span>
                                    {m.role && (
                                        <span className="d-block text-muted small text-capitalize">
                                            {m.role}
                                        </span>
                                    )}
                                </a>
                            </Col>
                        ))}
                    </Row>
                )}
            </Card.Body>
        </Card>
    );
}

PlatformContextCard.propTypes = {
    memberships: PropTypes.arrayOf(
        PropTypes.shape({
            ecosystem: PropTypes.shape({
                slug: PropTypes.string,
                name: PropTypes.string,
                customDomain: PropTypes.string,
                subdomain: PropTypes.string,
            }),
            role: PropTypes.string,
        })
    ),
    loading: PropTypes.bool,
    error: PropTypes.string,
};

PlatformContextCard.defaultProps = {
    memberships: [],
    loading: false,
    error: null,
};
